import { z } from 'zod'
import type { WrappedItem } from '@integration-layer/components/Algolia/AlgoliaAisInfiniteHits.vue'
import type { AlgoliaProductType } from '@integration-layer/components/Algolia/AlgoliaAisInfiniteHits.props'
import type { EditorialContainerData } from '@integration-layer/components/Editorial/EditorialContainer.props'
import { viewItemListSchema } from '@integration-layer/schemas/GAEvents/viewItemList'
import { itemSchema } from '@integration-layer/schemas/GAEvents/index'
import snakeCase from 'lodash/snakeCase'
import { extractCategoriesUnlocalized } from '@design-system/utils/formatAlgoliaHierarchicalCategories'

type ViewItemList = z.infer<typeof viewItemListSchema>
type AvailableItem = z.infer<typeof itemSchema>
type BooleanLiteral = 0 | 1

interface plpItem {
  TotalQty?: number
  availableSizes?: string
  discount?: boolean
  item_reservable?: boolean
}

/**
 * Generates Google Analytics 4 view item list events for product listing pages.
 *
 * This composable prepares and sends tracking events for product lists,
 * breaking down large lists into manageable chunks for analytics.
 *
 * @param {WrappedItem[]} plpProducts - Array of products from the product listing page
 * @param {number} currentHits - Current number of hits/products already processed
 * @param {boolean} [waitForDatalayerSetup] - Optional flag to wait for datalayer setup
 *
 * @returns {void}
 *
 * @throws {Error} Logs an error if the input products array is invalid
 *
 * @example
 * // Track products on a product listing page
 * useGAViewItemListEvent(productList, 0, true)
 *
 * @remarks
 * - Filters out non-product items
 * - Chunks product lists into groups of 18 for Google Analytics
 * - Handles brand, category, and product detail formatting
 * - Supports various product metadata extraction
 */
export const useGAViewItemListEvent = (
  plpProducts: WrappedItem[],
  currentHits: number,
  waitForDatalayerSetup?: boolean
) => {
  // Input validation
  if (!Array.isArray(plpProducts)) {
    console.error('Invalid or missing plp array.')
    return
  }

  const productList: Array<AvailableItem> = []

  // Process each product in the list
  plpProducts.slice(currentHits).forEach(item => {
    if (item.type !== 'product') return

    const plpItem = Object.fromEntries(
      Object.entries(item.content).map(([k, v]) => [k, v])
    ) as AlgoliaProductType & plpItem

    // Extract categories and route helper
    const { category1, category2, category4 } = extractCategoriesUnlocalized(
      plpItem?.hierarchicalCategories || [],
      plpItem?.categoryPageId2 || []
    )

    const { pdpPath } = useRouteHelper()

    // Prepare product details for GA event
    productList.push({
      affiliation: 'armani.com',
      coupon: '',
      discount: 0 as BooleanLiteral,
      index: currentHits + productList.length + 1,
      item_availability: (plpItem?.TotalQty ?? 0) > 0 ? 1 : 0,
      item_brand:
        plpItem.Brand?.toLowerCase() === 'ea7'
          ? 'EA7'
          : snakeCase(plpItem.Brand ?? 'armani group'),
      item_category: snakeCase(category1) ?? '',
      item_category2: snakeCase(category2) ?? '',
      item_category4: snakeCase(category4) ?? '',
      item_discount: 0 as BooleanLiteral,
      item_id: plpItem.objectID ?? '',
      item_img: plpItem.assets?.[0].URL ?? '',
      item_list_id: '',
      item_list_name: snakeCase(`${plpItem.Brand}-${category2}`) ?? '',
      item_mfc_id: plpItem.MFC ?? '',
      item_name: plpItem.Name ?? '',
      item_reservable: plpItem.item_reservable ? 1 : 0,
      item_size: plpItem.availableSizes?.[0] ?? '',
      item_variant: plpItem.ColorDesc ?? '',
      price: plpItem.FullPrice ?? 0,
      quantity: plpItem.TotalQty ?? 0,
      item_url: useRequestURL().origin + pdpPath(plpItem),
    })
  })

  // Prevent processing empty product lists
  if (productList.length === 0) return

  // Split into chunks of 18 products and send events
  const chunkedProductList = chunk(productList, 18)
  chunkedProductList.forEach(chunk => {
    const event: ViewItemList = {
      event: 'view_item_list',
      eventID: '001',
      ecommerce: {
        item_list_id: 'view_item_list_plp',
        item_list_name: 'view_item_list_product_listing_page',
        items: chunk,
      },
    }

    useGASendEvent(viewItemListSchema, event, {
      clearEcommerce: true,
      waitForDatalayerSetup,
    })
  })
}
